import { Link } from "react-router-dom";
import type { UnifiedContent } from "@/types/content";
import CategoryBadge from "@/components/base/CategoryBadge";
import { formatDate } from "@/utils/content";

interface ContentCardProps {
  item: UnifiedContent;
  layout?: "grid" | "list";
}

export default function ContentCard({ item, layout = "grid" }: ContentCardProps) {
  const isCharacter = item.category === "characters";
  const imageRatio = isCharacter ? "h-52" : "h-44";

  if (layout === "list") {
    return (
      <Link
        to={`/detail/${item.id}`}
        className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 md:p-4"
      >
        <div className={`w-28 shrink-0 overflow-hidden rounded-md bg-background-200 sm:w-40 ${imageRatio}`}>
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} - 我妻子的一切`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="mb-1.5 flex items-center gap-2">
            <CategoryBadge category={item.category} />
            {item.date && <span className="text-xs text-foreground-400">{formatDate(item.date)}</span>}
          </div>
          <h3 className="line-clamp-2 font-heading text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-700">
            {item.title}
          </h3>
          <p className="mt-1.5 line-clamp-2 text-sm leading-6 text-foreground-500">{item.summary}</p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${item.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className={`w-full overflow-hidden bg-background-200 ${imageRatio}`}>
        <img
          src={item.cover}
          alt={item.title}
          title={`${item.title} - 我妻子的一切`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="mb-2 flex items-center gap-2">
          <CategoryBadge category={item.category} />
          {item.date && <span className="text-xs text-foreground-400">{formatDate(item.date)}</span>}
        </div>
        <h3 className="line-clamp-2 font-heading text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
          {item.title}
        </h3>
        <p className="mt-2 line-clamp-3 flex-1 text-sm leading-6 text-foreground-500">
          {item.summary}
        </p>
        {item.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {item.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="rounded bg-background-100 px-2 py-0.5 text-xs text-foreground-500"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}